import { useMutation } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateProfileInformation } from 'api/user';
import { sendVerifyEmail } from 'api/verification';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { isEmail } from 'helpers/regex';
import { twMerge } from 'tailwind-merge';
import { GivebutterAxiosError } from 'types/error';

interface EmailFormValues {
    email: string;
}

interface UpdateEmailFormDialogProps extends Dialog.DialogProps {}

const UpdateEmailFormDialog = ({ children, ...rest }: UpdateEmailFormDialogProps) => {
    const toast = useToast();
    const { id: userId, firstName, lastName, email } = useCurrentUser();
    const { accountId } = useCurrentAccount();

    const methods = Form.useGivebutterForm<EmailFormValues>({
        defaultValues: {
            email,
        },
    });

    const {
        formState: { isDirty, isValid },
        handleSubmit,
        control,
        path,
        getValues,
    } = methods;

    const {
        mutate: submit,
        isPending: isSubmitting,
        isSuccess: emailWasUpdated,
    } = useMutation({
        mutationFn: (values: EmailFormValues) =>
            updateProfileInformation(firstName, lastName, values.email),
        onSuccess: () => {
            sendVerificationEmail();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Failed to update email');
        },
    });

    const { mutate: sendVerificationEmail, isPending: isSendingVerificationEmail } = useMutation({
        mutationFn: () => sendVerifyEmail(userId, accountId),
        onSuccess: () => {
            toast.success('Verification email sent!');
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Something went wrong. Please try again.');
        },
    });

    return (
        <Dialog.Root {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-[400px] p-6">
                    <Dialog.Header>
                        <div
                            className={twMerge(
                                'relative flex aspect-square size-12 items-center justify-center rounded-full bg-gray-100 text-gray-100',
                                'after:-z-layerOne after:absolute after:-inset-1 after:rounded-full after:bg-gray-100 after:p-2',
                                emailWasUpdated && 'bg-green-100 text-green-100 after:bg-green-100',
                            )}
                        >
                            <FontAwesomeIcon
                                icon={emailWasUpdated ? faCheck : faEdit}
                                className={twMerge(
                                    'size-6 text-gray-500',
                                    emailWasUpdated && 'text-green-500',
                                )}
                            />
                        </div>
                        <Dialog.Close />
                    </Dialog.Header>
                    {!emailWasUpdated && (
                        <Form.Provider {...methods}>
                            <form onSubmit={handleSubmit(values => submit(values))}>
                                <div className="flex flex-col gap-4">
                                    <div className="flex w-full flex-col gap-6">
                                        <Dialog.Title>Update your information</Dialog.Title>
                                        <Form.Container>
                                            <Form.Label name={path('email')}>Email</Form.Label>
                                            <Form.TextInputRoot
                                                control={control}
                                                name="email"
                                                rules={{
                                                    required: 'Email is required',
                                                    pattern: {
                                                        value: isEmail,
                                                        message: 'Invalid email address',
                                                    },
                                                }}
                                            >
                                                <Form.TextInputForm placeholder="email@example.com" />
                                            </Form.TextInputRoot>
                                            <Form.Error name={path('email')} />
                                        </Form.Container>
                                    </div>

                                    <AlertNotification.Root type="warning">
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Description>
                                                This will change the email you use to log into
                                                Givebutter.
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>

                                    <div className="grid grid-cols-2 gap-3">
                                        <Dialog.Cancel asChild>
                                            <Button variant="outline" color={'neutral'}>
                                                Cancel
                                            </Button>
                                        </Dialog.Cancel>
                                        <Form.SubmitButton
                                            isSubmitting={isSubmitting}
                                            disabled={!isDirty || !isValid}
                                        >
                                            Update
                                        </Form.SubmitButton>
                                    </div>
                                </div>
                            </form>
                        </Form.Provider>
                    )}
                    {emailWasUpdated && (
                        <div className="flex flex-col gap-4">
                            <Dialog.Title className="text-xl">
                                Email successfully updated
                            </Dialog.Title>
                            <p>
                                Please check {getValues('email')} for a verification email. You may
                                need to check your spam folder or wait a few minutes before
                                retrying.
                            </p>

                            <div className="flex flex-col gap-3">
                                <Dialog.Cancel asChild>
                                    <Button variant="solid">Done</Button>
                                </Dialog.Cancel>
                                <Button
                                    variant="transparent"
                                    color="neutral"
                                    disabled={isSendingVerificationEmail}
                                    onClick={() => sendVerificationEmail()}
                                >
                                    {isSendingVerificationEmail && (
                                        <FontAwesomeIcon
                                            className="animate-spin"
                                            icon={faSpinnerThird}
                                        />
                                    )}
                                    Resend verification email
                                </Button>
                            </div>
                        </div>
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

interface UpdateEmailFormDialogTriggerProps
    extends Omit<Dialog.DialogTriggerProps, 'color' | 'asChild'> {}
const UpdateEmailFormDialogTrigger = ({ children, ...rest }: UpdateEmailFormDialogTriggerProps) => {
    return (
        <Dialog.Trigger asChild {...rest}>
            {children}
        </Dialog.Trigger>
    );
};

const Root = UpdateEmailFormDialog;
const Trigger = UpdateEmailFormDialogTrigger;

export {
    UpdateEmailFormDialog,
    UpdateEmailFormDialogTrigger,
    //
    Root,
    Trigger,
};
